import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../api';
import { MARQUE } from '../marque';
import { IconeCorbeille } from '../components/Icones';

const NAVY = '#1a2b4a';
const BORDER = '#e5e7eb';

// Mot de passe d'accès à l'espace client. Tiré de crypto.getRandomValues et
// non de Math.random(), dont la suite est prévisible à partir de quelques
// valeurs observées — pour un tirage de mot de passe, ce n'est pas anodin.
// 16 caractères : au-delà du minimum imposé par l'API, sans coût pour le
// client puisque le mot de passe est copié-collé.
function genPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!';
  const tirage = new Uint32Array(16);
  window.crypto.getRandomValues(tirage);
  return Array.from(tirage, (n) => chars[n % chars.length]).join('');
}

function Badge({ actif }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600',
      background: actif ? '#dcfce7' : '#fee2e2', color: actif ? '#16a34a' : '#dc2626',
    }}>
      {actif ? 'Actif' : 'Inactif'}
    </span>
  );
}

export default function ClientsWeb() {
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [form, setForm] = useState({ nom: '', email: '', mot_de_passe: '', prospect_id: '' });
  const [searchP, setSearchP] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/clients-web`),
        axios.get(`${API}/prospects`),
      ]);
      setClients(c.data);
      setProspects(p.data.filter(p => p.type === 'client' || p.type === 'prospect'));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    const pwd = genPassword();
    setForm({ nom: '', email: '', mot_de_passe: pwd, prospect_id: '' });
    setSearchP('');
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.nom.trim() || !form.email.trim() || !form.mot_de_passe.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/clients-web`, {
        nom: form.nom,
        email: form.email,
        mot_de_passe: form.mot_de_passe,
        prospect_id: form.prospect_id ? parseInt(form.prospect_id) : null,
      });
      await load();
      setShowCreate(false);
      showToast('Accès client créé');
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la création');
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!resetTarget || !newPwd.trim()) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/clients-web/${resetTarget.id}`, { mot_de_passe: newPwd });
      setResetTarget(null);
      showToast('Mot de passe réinitialisé');
    } catch (e) {
      // Le serveur refuse les mots de passe faibles : afficher son message
      // plutôt qu'un « Erreur » qui laisserait croire à une panne.
      alert(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleToggleActif = async (client) => {
    try {
      await axios.patch(`${API}/clients-web/${client.id}`, { actif: !client.actif });
      setClients(cs => cs.map(c => c.id === client.id ? { ...c, actif: !c.actif } : c));
    } catch { alert('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/clients-web/${id}`);
      setClients(cs => cs.filter(c => c.id !== id));
      setDeleteConfirm(null);
      showToast('Accès supprimé');
    } catch { alert('Erreur'); }
  };

  const copyCredentials = (client, pwd) => {
    const text = `Accès espace client ${MARQUE.nom}\nURL : ${MARQUE.urlPubliqueCRM}\nEmail : ${client.email}\nMot de passe : ${pwd || '(non disponible)'}`;
    navigator.clipboard.writeText(text).then(() => showToast('Identifiants copiés'));
  };

  const filteredProspects = prospects.filter(p =>
    p.nom_societe.toLowerCase().includes(searchP.toLowerCase())
  ).slice(0, 50);

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: `1.5px solid ${BORDER}`,
    borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 9999,
          background: '#1a2b4a', color: '#fff', padding: '12px 20px', borderRadius: '10px',
          fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>{toast}</div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: NAVY, margin: 0 }}>Clients Web</h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            Gérer les accès à l'espace client en ligne
          </p>
        </div>
        <button onClick={openCreate} style={{
          background: NAVY, color: '#fff', border: 'none', borderRadius: '8px',
          padding: '10px 20px', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
        }}>
          + Créer accès client
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>Chargement…</div>
        ) : clients.length === 0 ? (
          <div style={{ padding: '64px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌐</div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Aucun accès client</div>
            <div style={{ fontSize: '13px' }}>Cliquez sur "+ Créer accès client" pour commencer</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${BORDER}` }}>
                {['Entreprise', 'Nom / Email', 'Date création', 'Statut', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c, i) => (
                <tr key={c.id} style={{ borderBottom: i < clients.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                  <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: NAVY }}>
                    {c.nom_societe_liee || <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{c.nom}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{c.email}</div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {c.date_creation ? new Date(c.date_creation).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <button onClick={() => handleToggleActif(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <Badge actif={c.actif} />
                    </button>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        title="Réinitialiser le mot de passe"
                        onClick={() => { setNewPwd(genPassword()); setResetTarget(c); }}
                        style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Reset
                      </button>
                      <button
                        title="Copier les identifiants"
                        onClick={() => copyCredentials(c, null)}
                        style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Copier
                      </button>
                      <button
                        title="Supprimer l'accès"
                        onClick={() => setDeleteConfirm(c)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}><IconeCorbeille taille={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal Création ── */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: NAVY, margin: '0 0 24px' }}>Créer un accès client</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Entreprise liée</label>
                <input
                  placeholder="Rechercher une entreprise…"
                  value={searchP}
                  onChange={e => { setSearchP(e.target.value); setForm(f => ({ ...f, prospect_id: '' })); }}
                  style={inputStyle}
                />
                {searchP && !form.prospect_id && (
                  <div style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    {filteredProspects.length === 0 ? (
                      <div style={{ padding: '12px', color: '#94a3b8', fontSize: '13px' }}>Aucun résultat</div>
                    ) : filteredProspects.map(p => (
                      <div key={p.id}
                        onClick={() => { setForm(f => ({ ...f, prospect_id: p.id })); setSearchP(p.nom_societe); }}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', borderBottom: `1px solid ${BORDER}` }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        {p.nom_societe}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Nom du contact *</label>
                <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Jean Dupont" style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean@entreprise.fr" style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Mot de passe *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={form.mot_de_passe} onChange={e => setForm(f => ({ ...f, mot_de_passe: e.target.value }))} style={{ ...inputStyle, fontFamily: 'monospace' }} />
                  <button onClick={() => setForm(f => ({ ...f, mot_de_passe: genPassword() }))}
                    style={{ padding: '9px 12px', background: '#f8fafc', border: `1.5px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}
                    title="Générer un mot de passe">🔄</button>
                </div>
              </div>

              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#0369a1' }}>Pensez à copier les identifiants avant de fermer cette fenêtre.
                <button
                  onClick={() => {
                    const txt = `Accès espace client ${MARQUE.nom}\nURL : ${MARQUE.urlPubliqueCRM}\nEmail : ${form.email}\nMot de passe : ${form.mot_de_passe}`;
                    navigator.clipboard.writeText(txt).then(() => showToast('Identifiants copiés'));
                  }}
                  style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontWeight: '700', fontSize: '13px', textDecoration: 'underline' }}>Copier maintenant
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '10px 20px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Annuler
              </button>
              <button onClick={handleCreate} disabled={saving} style={{ padding: '10px 20px', background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Création…' : "Créer l'accès"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Reset mot de passe ── */}
      {resetTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: NAVY, margin: '0 0 8px' }}>Réinitialiser le mot de passe</h2>
            <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 20px' }}>
              Client : <strong>{resetTarget.email}</strong>
            </p>
            <label style={labelStyle}>Nouveau mot de passe</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input value={newPwd} onChange={e => setNewPwd(e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} />
              <button onClick={() => setNewPwd(genPassword())} style={{ padding: '9px 12px', background: '#f8fafc', border: `1.5px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>🔄</button>
            </div>
            <button
              onClick={() => {
                const txt = `Accès espace client ${MARQUE.nom}\nURL : ${MARQUE.urlPubliqueCRM}\nEmail : ${resetTarget.email}\nMot de passe : ${newPwd}`;
                navigator.clipboard.writeText(txt).then(() => showToast('Identifiants copiés'));
              }}
              style={{ width: '100%', padding: '9px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#15803d', fontWeight: '600', fontSize: '13px', cursor: 'pointer', marginBottom: '16px' }}>Copier les nouveaux identifiants
            </button>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setResetTarget(null)} style={{ padding: '10px 20px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Annuler
              </button>
              <button onClick={handleReset} disabled={saving} style={{ padding: '10px 20px', background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>
                {saving ? '…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Suppression ── */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626', margin: '0 0 12px' }}>Supprimer l'accès ?</h2>
            <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 24px', lineHeight: 1.6 }}>
              L'accès de <strong>{deleteConfirm.email}</strong> sera définitivement supprimé.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '10px 20px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Annuler
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} style={{ padding: '10px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
